import { CSSProperties, memo, useLayoutEffect, useRef, useState } from "react";

import { cn } from "@/components/ui/utils";

import { typographyStyles } from "@/components/ui/typography";

const TRANSITION_DURATION = 250;

/**
 * The boxes a commit drew, waiting to be measured together once its effects
 * have run: each measured on its own forced the page's layout again after the
 * last one's width was set - a table's every chip, on every row scrolled to.
 */
const boxesToMeasure = new Set<{
  container: HTMLDivElement;
  content: HTMLDivElement;
}>();

interface BoxWithAdornmentProps {
  children: React.ReactNode;
  endAdornment?: React.ReactNode;
  onChildrenClick?: () => void;
  disabled?: boolean;
  delta?: number;
  style?: CSSProperties;
  fontKey?: string;
  isOpenOverride?: boolean;
  /** When true, skip intrinsic-width locking and stretch to the parent width. */
  fillWidth?: boolean;
}

const BoxWithAdornment = memo(function BoxWithAdornment({
  children,
  endAdornment,
  onChildrenClick,
  disabled = false,
  delta = 24,
  style,
  fontKey = "body-md",
  isOpenOverride,
  fillWidth = false,
}: BoxWithAdornmentProps) {
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const adornmentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasMounted = useRef(false);

  const isOpen = isOpenOverride ?? (disabled ? false : isHovering);

  // A box drawn for the first time takes no transition, so its first position
  // needs no layout forced to settle it.
  useLayoutEffect(() => {
    hasMounted.current = true;
  }, []);

  useLayoutEffect(() => {
    if (!hasMounted.current) return;
    if (!contentRef.current) return;
    // Ensure text-overflow is set correctly after transitions
    if (isOpen) {
      requestAnimationFrame(() => {
        if (!contentRef.current) return;
        contentRef.current.style["text-overflow"] = "ellipsis";
      });
    } else {
      setTimeout(() => {
        if (!contentRef.current) return;
        contentRef.current.style["text-overflow"] = "ellipsis";
      }, TRANSITION_DURATION);
    }
  }, [isOpen]);

  // Measure the natural content width once on mount and when what it shows
  // changes - its children are new at every render, and a measure forces a
  // layout, which in Safari is the whole page's at every key typed beside it.
  // Skip for table cells that should stretch to the full column width.
  const measured = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (fillWidth) return;
    if (!contentRef.current || !containerRef.current) return;
    // Only measure when not in "open" state to get the true natural width
    if (isOpen) return;
    // Its letters drawn in the fallback until the fonts are in.
    const shown = `${document.fonts.status} ${fontKey} ${contentRef.current.innerHTML}`;
    if (shown === measured.current) return;
    measured.current = shown;
    boxesToMeasure.add({
      container: containerRef.current,
      content: contentRef.current,
    });
    if (boxesToMeasure.size > 1) return;
    // Still before the page is painted: every box reset to fit-content, then
    // every natural width read - one layout - then every width locked.
    queueMicrotask(() => {
      const boxes = [...boxesToMeasure];
      boxesToMeasure.clear();
      for (const { container } of boxes) {
        container.style.width = "fit-content";
      }
      const naturalWidths = boxes.map(({ content }) => content.scrollWidth);
      boxes.forEach(({ container }, index) => {
        container.style.width = `${naturalWidths[index] + 1}px`;
      });
    });
  }, [children, fillWidth, fontKey, isOpen]);

  const handleMouseEnter = () => {
    setIsHovering(true);
  };
  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        overflow: "clip",
        width: fillWidth ? "100%" : "fit-content",
        ...style,
      }}
    >
      <div
        ref={contentRef}
        onClick={onChildrenClick}
        // Narrowed by the adornment's room as it slides in.
        className={cn(
          "inline-block whitespace-nowrap overflow-hidden text-left text-ellipsis [transition:all_250ms_ease]",
          isOpen ? "w-[calc(100%-var(--adornment-room))]" : "w-full",
        )}
        style={
          {
            "--adornment-room": `${delta}px`,
            // Its level's letters, as the theme has them.
            ...((typographyStyles as Record<string, React.CSSProperties>)[
              fontKey as keyof typeof typographyStyles
            ] as CSSProperties | undefined),
          } as CSSProperties
        }
      >
        {children}
      </div>
      <div
        ref={adornmentRef}
        style={{
          padding: "2px",
          position: "absolute",
          right: 0,
          display: "inline-block",
          justifyContent: "center",
          alignItems: "center",
          width: delta,
          height: delta,
          transition: `transform ${TRANSITION_DURATION}ms ease`,
          transform: `translateX(${isOpen ? -2 : delta + 2}px)`,
        }}
      >
        {endAdornment}
      </div>
    </div>
  );
});

export { BoxWithAdornment };
